'use client';

import { faArrowLeft, faSearch, faTimes } from '@fortawesome/pro-light-svg-icons';
import { Activity } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useStore } from 'zustand';

import { SearchResults } from '@/app/(main)/_components/search-modal/_components/search-results.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { SheetClose, SheetHeader } from '@/components/ui/sheet';
import { MostSearched } from '@/app/(main)/_components/search-modal/_components/most-searched.component';
import { SearchFilters } from '@/app/(main)/_components/search-modal/_components/search-filters.component';
import { SearchSkeletonLoading } from '@/app/(main)/_components/search-modal/_components/loadings/search-skeleton-loading.component';
import { RecentSearched } from '@/app/(main)/_components/search-modal/_components/recent-searched.component';
import { SearchEmptyState } from '@/app/(main)/_components/search-modal/_components/search-empty-state.component';
import { useSearchModalFetch } from '@/app/(main)/_components/search-modal/_hooks/use-search-modal-fetch.hook';
import { useStickyHeader } from '@/app/(main)/_components/search-modal/_hooks/use-sticky-header.hook';
import { SearchType, SelectedProcedures } from '@/app/(main)/_components/search-modal/search-modal.types';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { useSearchState } from '@/store/search/search.store';

type SearchModalDefaultProps = {
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  setOpen: (open: boolean) => void;
  locationOverride?: OverridedUserLocation;
};

export function SearchModalDefault({
  selectLaboratoryProcedure,
  setOpen,
  locationOverride,
}: SearchModalDefaultProps) {
  const { containerRef, headerCallbackRef, isScrolled } = useStickyHeader();
  const {
    inputValue,
    searchDisplayValue,
    handleSearchInput,
    allResults,
    shouldShowSearchResults,
    shouldShowEmptyState,
    searchResultIsLoading,
    handleRemoveSelectedSearch,
  } = useSearchModalFetch();

  const selectedFilter
    = useStore(useSearchState, (state) => state?.selectedFilter) ?? SearchType.ALL;

  return (
    <div
      className="flex flex-col h-full"
      data-testid={DataTestIdEnum.PROCEDURES_MODAL_CONTAINER}
    >
      <SheetHeader
        ref={headerCallbackRef}
        className={`
          sticky top-0 left-0 right-0 z-20
          px-6 pt-5
          transition-colors duration-200
          ${isScrolled ? 'bg-gray-50 shadow-floating pb-3' : 'bg-white'}
        `}
      >
        <div className="md:flex justify-end gap-3 items-center hidden md:visible">
          <SheetClose
            asChild
            className="text-gray-500 cursor-pointer self-center hover:bg-gray-50"
          >
            <Icon
              icon={faTimes}
              size="xl"
              className="hover:text-gray-900 text-[1.625rem]"
            />
          </SheetClose>
        </div>
        <div className="mb-4 pt-2 flex gap-4">
          <div className="md:hidden flex justify-center align-middle">
            <Icon
              icon={faArrowLeft}
              size="xl"
              className="text-gray-500 self-center cursor-pointer"
              onClick={() => setOpen(false)}
            />
          </div>
          <Input
            placeholder="O que você procura?"
            startIcon={faSearch}
            startIconStyle="text-gray-500 text-xl"
            value={searchDisplayValue}
            onChange={handleSearchInput}
            onClear={handleRemoveSelectedSearch}
            data-testid={DataTestIdEnum.PROCEDURES_MODAL_INPUT}
            cleanValueDataTestId={DataTestIdEnum.PROCEDURES_MODAL_INPUT_CLEAR}
          />
        </div>

        {shouldShowSearchResults && !shouldShowEmptyState && (
          <SearchFilters
            className="!mt-4 -mx-6 md:mx-0"
            results={allResults}
          />
        )}
      </SheetHeader>

      <div ref={containerRef} className="overflow-y-auto">
        {!shouldShowEmptyState && !shouldShowSearchResults && !searchResultIsLoading && (
          <div className="flex flex-col gap-4 mt-4 pb-5">
            <RecentSearched
              setOpen={setOpen}
              selectLaboratoryProcedure={selectLaboratoryProcedure}
              locationOverride={locationOverride}
            />
            <MostSearched
              setOpen={setOpen}
              selectLaboratoryProcedure={selectLaboratoryProcedure}
              locationOverride={locationOverride}
            />
          </div>
        )}

        {shouldShowEmptyState && (
          <div className="px-6 pb-5">
            <SearchEmptyState setOpen={setOpen} searchTerm={inputValue} />
          </div>
        )}

        {shouldShowSearchResults && !shouldShowEmptyState && selectedFilter && (
          <SearchResults
            key={selectedFilter}
            searchTerm={inputValue}
            searchType={selectedFilter as SearchType}
            results={allResults[selectedFilter]}
            setOpen={setOpen}
            selectLaboratoryProcedure={selectLaboratoryProcedure}
            locationOverride={locationOverride}
          />
        )}

        <Activity mode={searchResultIsLoading ? 'visible' : 'hidden'}>
          <div className="px-6 pb-5">
            <SearchSkeletonLoading />
          </div>
        </Activity>
      </div>
    </div>
  );
}
